import React from 'react';

import Dropdown from 'components/cosmos/dropdown';
import Loading from 'components/cosmos/loading';
import { Radio } from 'components/cosmos/radio';
import { ReactComponent as ArrowRight } from 'components/svg/icons/arrow-right.svg';
import { translate } from 'components/translate';
import {
  EVENT_ACTIONS,
  EVENTS_INTERACTIONS,
  EVENTS_CATEGORY,
  EVENTS_LABELS
} from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { buildInteractionEventData } from 'modules/app/services/datahub/helper';
import PropTypes from 'prop-types';

import {
  SCHEDULER_OPTIONS,
  CUSTOM_DAYS_SELECTOR,
  UNDEFINED,
  DAYS,
  CUSTOM,
  PREVIOUS,
  BEGIN
} from './constants';
import SchedulerHeader from './SchedulerHeader';

import { LIBERATION_TYPES } from '../../../../helpers/Dripping';
import {
  clickDaysSelector,
  clickExpirationDaysSelector,
  LABEL_DAYS
} from '../DrippingBulkActionEvents';

import './DaysSelector.scss';

export const DAYS_SELECTOR_FORM = 'DAYS_SELECTOR_FORM';

const UNDEFINED_OPTION = { label: 'general.undefined', type: UNDEFINED };

export const OPTIONS = [
  { value: 7, type: DAYS },
  { value: 15, type: DAYS },
  { value: 30, type: DAYS }
];

const DaysSelector = React.memo(
  ({ type, transition, checkedContent, t, toogle, updateWizardState }) => {
    const [sending, setSending] = React.useState(false);
    const options =
      type === LIBERATION_TYPES.EXPIRATION
        ? [UNDEFINED_OPTION, ...OPTIONS]
        : OPTIONS;
    const { title, wizard, request } = SCHEDULER_OPTIONS[type] || {};

    const sendEvents = (days) => {
      if (type === 'EXPIRATION') {
        clickExpirationDaysSelector(days || 'UNDEFINED');

        DatahubService.sendEvent({
          action: EVENT_ACTIONS.INTERACT,
          event: buildInteractionEventData({
            category:
              EVENTS_CATEGORY.CONTENT.CONTENT_SCHEDULE
                .CONTENT_SCHEDULE_EXPIRATION,
            action: EVENTS_INTERACTIONS.CLICK,
            label:
              EVENTS_LABELS.CONTENT.CONTENT_SCHEDULE.DAYS[
                LABEL_DAYS[days || 'UNDEFINED']
              ]
          })
        });
      } else {
        clickDaysSelector(days);

        DatahubService.sendEvent({
          action: EVENT_ACTIONS.INTERACT,
          event: buildInteractionEventData({
            category:
              EVENTS_CATEGORY.CONTENT.CONTENT_SCHEDULE
                .CONTENT_SCHEDULE_DRIPPING_BY_DAY,
            action: EVENTS_INTERACTIONS.CLICK,
            label:
              EVENTS_LABELS.CONTENT.CONTENT_SCHEDULE.DAYS[
                LABEL_DAYS[days || 'UNDEFINED']
              ]
          })
        });
      }
    };

    const goToBegin = () => {
      updateWizardState && updateWizardState(BEGIN);

      transition(BEGIN, { type });
    };

    const closeDropdown = () => {
      toogle && toogle();

      Dropdown.closeMenu();

      goToBegin();
    };

    const setDripping = async ({ type: optionType, value }) => {
      try {
        if (optionType === PREVIOUS) {
          updateWizardState && updateWizardState(PREVIOUS);

          transition(PREVIOUS, { type });

          return;
        }

        if (optionType === CUSTOM) {
          updateWizardState && updateWizardState(CUSTOM_DAYS_SELECTOR);

          transition(CUSTOM_DAYS_SELECTOR, { type });

          sendEvents(CUSTOM);

          return;
        }

        closeDropdown();

        setSending(true);

        request && (await request(value || optionType, checkedContent));
      } finally {
        setSending(false);
      }
    };

    const goToPrevious = (e) => {
      e.stopPropagation();

      setDripping({ type: PREVIOUS });
    };

    const goToCustom = (e) => {
      e.stopPropagation();

      setDripping({ type: CUSTOM });
    };

    return (
      <>
        {sending && <Loading data-test="ca-days-selector-loading" />}
        <SchedulerHeader
          goToPrevious={goToPrevious}
          description={wizard}
          closeDropdown={closeDropdown}
          title={title}
        />
        <form name="DAYS_SELECTOR_FORM">
          {options.map((option, index) => {
            const value = option.value || option.type;

            return (
              <Radio
                name="days-selector-option"
                id={`${DAYS_SELECTOR_FORM}_${value}`}
                className="_ml-5 _mr-5"
                key={`${value}_${index}`}
                label={t(option.label || 'general.n_days_plural', {
                  data: {
                    days: option.value,
                    count: option.value
                  }
                })}
                value={value}
                onClick={(e) => {
                  e.stopPropagation();
                  setDripping(option);
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                  sendEvents(option.value);
                }}
              />
            );
          })}
          <Dropdown.MenuItem
            className="_pb-4 _pt-4 _pl-5 _pr-5"
            onClick={goToCustom}
            role="button"
          >
            <span className="days-selector__option _d-flex _justify-content-between _text-2 _text-gray-700">
              {t('general.other')}
              <ArrowRight className="_mt-1" />
            </span>
          </Dropdown.MenuItem>
        </form>
      </>
    );
  }
);

DaysSelector.propTypes = {
  type: PropTypes.string,
  t: PropTypes.func,
  transition: PropTypes.func,
  checkedContent: PropTypes.object,
  toogle: PropTypes.func,
  updateWizardState: PropTypes.func
};

export default translate()(DaysSelector);
